import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Facebook,
  Plus,
  // MessageSquare, // Commented out for Facebook review
  CheckCircle,
  AlertCircle,
  Loader2,
  RefreshCw,
  Trash2,
  XCircle,
  Settings,
  Instagram,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import Progress from "../components/ui/progress";
import { useSubscription } from "../contexts/SubscriptionContext";
import { Switch } from "../components/ui/switch";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useApi, API_ENDPOINTS } from "../lib/api";
import { useFacebookPage } from "../contexts/FacebookPageContext";
import { useInstagramEnabled } from "../config/featureFlags";

interface FacebookPage {
  id: string;
  name: string;
  platform?: "facebook" | "instagram";
  username?: string;
  category: string;
  followers: string;
  status: string;
  isActive?: boolean;
  isConnected?: boolean;
  deactivatedAt?: string | null;
  disconnectedAt?: string | null;
  lastActivity: string;
  messages: number;
  accessToken?: string;
  prompt?: string;
  picture?: string;
  aiSettings?: {
    autoRepliesEnabled?: boolean;
    commentModerationEnabled?: boolean;
    languagePreference?: string;
    replyDelay?: number; // -1 represents "real" mode with random delay
  };
  canReconnect?: boolean;
  dataPreserved?: boolean;
}

const FacebookPages: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading } = useAuth0();
  const api = useApi();
  const location = useLocation();
  const navigate = useNavigate();
  const { subscription, canConnectPage, refresh: refreshSubscription } = useSubscription();
  const { setSelectedPage } = useFacebookPage();

  const [pages, setPages] = useState<FacebookPage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [showLimitDialog, setShowLimitDialog] = useState(false);
  const [isConnectingInstagram, setIsConnectingInstagram] = useState(false);
  const instagramEnabled = useInstagramEnabled();
  const [removingPageId, setRemovingPageId] = useState<string | null>(null);
  const [manageDialogPageId, setManageDialogPageId] = useState<string | null>(null);
  // COMMENTED OUT FOR FACEBOOK REVIEW - Preserved for re-enabling after approval
  const [_togglingPageId, _setTogglingPageId] = useState<string | null>(null);
  const [togglingModerationId, setTogglingModerationId] = useState<
    string | null
  >(null);

  // Handle redirect state
  const redirectState = location.state as {
    from?: string;
    message?: string;
  } | null;

  useEffect(() => {
    if (!authLoading && isAuthenticated && !isLoading) {
      loadPages();
    }
  }, [authLoading, isAuthenticated]);

  const loadPages = async () => {
    // Prevent multiple concurrent calls
    if (isLoading) {
      console.log("📝 [PAGES] Already loading, skipping...");
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      console.log("📝 [PAGES] Loading Facebook pages...");

      const response = await api.get(API_ENDPOINTS.FACEBOOK_PAGES);

      // Backend now always returns object with pages array
      const pagesData: FacebookPage[] = response.data.pages || [];
      const needsOAuth = response.data.needsOAuth || false;
      const message = response.data.message || "";

      console.log("📝 [PAGES] API Response:", {
        pagesCount: pagesData.length,
        needsOAuth,
        message,
      });

      if (needsOAuth) {
        console.log("🔄 [PAGES] User needs Facebook OAuth");
        setError("Please connect your Facebook account to access your pages.");
        setPages([]);
        return;
      }

      // If no pages found, retry after a delay
      if (pagesData.length === 0) {
        console.log("📝 No pages found, retrying in 2 seconds...");
        await new Promise((resolve) => setTimeout(resolve, 2000));

        // Retry the request
        console.log("🔄 Retrying pages fetch...");
        const retryResponse = await api.get(API_ENDPOINTS.FACEBOOK_PAGES);
        const retryPagesData: FacebookPage[] = retryResponse.data.pages || [];

        // Remove duplicates by page ID before setting
        const uniquePages = retryPagesData.filter(
          (page: FacebookPage, index: number, self: FacebookPage[]) =>
            index === self.findIndex((p) => p.id === page.id)
        );
        setPages(uniquePages);
      } else {
        // Remove duplicates by page ID before setting
        const uniquePages = pagesData.filter(
          (page: FacebookPage, index: number, self: FacebookPage[]) =>
            index === self.findIndex((p) => p.id === page.id)
        );
        setPages(uniquePages);
      }
      // Keep the plan page meter in step with connects/disconnects.
      refreshSubscription();
    } catch (error: any) {
      console.error("Error fetching Facebook pages:", error);
      if (error.response?.status === 400 && error.response?.data?.needsOAuth) {
        setError("Please connect your Facebook account to access pages.");
        // Optionally auto-redirect to OAuth flow
        console.log(
          "🔄 Facebook OAuth required, user needs to connect Facebook account"
        );
      } else if (error.response?.status === 401) {
        setError(
          "Authentication expired. Please refresh the page or log in again."
        );
      } else if (error.response?.status === 403) {
        setError("You do not have permission to access Facebook pages.");
      } else if (
        error.response?.status === 404 &&
        error.response?.data?.needsOAuth
      ) {
        setError("Please connect your Facebook account first to access pages.");
        console.log("🔄 [PAGES] User not found, needs Facebook OAuth first");
      } else if (error.code === "ERR_NETWORK") {
        setError(
          "Cannot connect to server. Please make sure the backend is running."
        );
      } else {
        setError(`Failed to fetch Facebook pages: ${error.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnectPage = async () => {
    // Plan gate. Applied inside the handlers rather than at each button, so
    // every entry point (header buttons, empty state, per-card reconnect) is
    // covered and any future one inherits it.
    if (!canConnectPage) {
      setShowLimitDialog(true);
      return;
    }
    try {
      setIsConnecting(true);
      const response = await axios.get(API_ENDPOINTS.FACEBOOK_OAUTH_START, {
        baseURL: process.env.REACT_APP_API_URL || "",
      });
      window.location.href = response.data.url;
    } catch (error: any) {
      console.error("Error starting Facebook OAuth:", error);
      setError(`Failed to start Facebook connection: ${error.message}`);
    } finally {
      setIsConnecting(false);
    }
  };

  // Instagram connect (beta-gated). Uses the authenticated api instance because
  // the IG start endpoint requires auth + the beta gate server-side.
  const handleConnectInstagram = async () => {
    // Plan gate. Applied inside the handlers rather than at each button, so
    // every entry point (header buttons, empty state, per-card reconnect) is
    // covered and any future one inherits it.
    if (!canConnectPage) {
      setShowLimitDialog(true);
      return;
    }
    try {
      setIsConnectingInstagram(true);
      const response = await api.get(API_ENDPOINTS.INSTAGRAM_OAUTH_START);
      window.location.href = response.data.url;
    } catch (error: any) {
      console.error("Error starting Instagram OAuth:", error);
      setError(`Failed to start Instagram connection: ${error.message}`);
    } finally {
      setIsConnectingInstagram(false);
    }
  };

  const handlePageCardClick = (page: FacebookPage, event: React.MouseEvent) => {
    // Prevent click if user clicked on a button or interactive element
    const target = event.target as HTMLElement;
    if (target.closest("button") || target.closest('[role="button"]')) {
      return;
    }

    // A disconnected card shows a "Reconnect" affordance instead — selecting it
    // would both break the destination and get remembered as the landing page.
    if (page.isConnected === false) {
      return;
    }

    // Set the selected page in context
    setSelectedPage({
      id: page.id,
      name: page.name,
      picture: page.picture,
      access_token: page.accessToken,
      platform: page.platform,
      username: page.username,
    });

    // Navigate directly to the comments page (the app's primary screen)
    navigate(`/accounts/${page.id}/comments`);
  };

  const handleRemovePage = async (pageId: string) => {
    const currentPage = pages.find((p) => p.id === pageId);
    if (!currentPage) return;

    const confirmed = window.confirm(
      `Disconnect "${currentPage.name}" from DeepChat?\n\n` +
        `• Page will be hidden from your dashboard\n` +
        `• All settings and data will be preserved\n` +
        `• You can restore it anytime by reconnecting\n\n` +
        `Note: To permanently delete all data, use "Delete Permanently" option.\n\n` +
        `Continue?`
    );

    if (!confirmed) return;

    setRemovingPageId(pageId);
    setManageDialogPageId(null); // Close dialog

    try {
      const response = await api.post(
        API_ENDPOINTS.FACEBOOK_PAGE_DEACTIVATE(pageId),
        {}
      );

      if (response.data.success) {
        // Remove the page from the list immediately
        setPages(pages.filter((page) => page.id !== pageId));
      } else {
        alert(
          `Failed to disconnect page: ${response.data.error || "Unknown error"}`
        );
      }
    } catch (error: any) {
      console.error("Error disconnecting page:", error);

      if (error.response?.status === 401) {
        alert(
          "Authentication expired. Please refresh the page and log in again."
        );
      } else if (error.response?.status === 404) {
        alert("Page not found. Please refresh the page list.");
      } else if (error.code === "ERR_NETWORK") {
        alert(
          "Cannot connect to server. Please make sure the backend is running."
        );
      } else {
        alert(
          `Failed to disconnect page: ${
            error.response?.data?.error || error.message
          }`
        );
      }
    } finally {
      setRemovingPageId(null);
    }
  };

  const handlePermanentDeletePage = async (pageId: string) => {
    const currentPage = pages.find((p) => p.id === pageId);
    if (!currentPage) return;

    // First confirmation
    const firstConfirm = window.confirm(
      `⚠️ PERMANENTLY DELETE "${currentPage.name}"?\n\n` +
        `This action CANNOT be undone and will:\n` +
        `• Delete ALL conversations and messages\n` +
        `• Delete ALL comments and posts\n` +
        `• Delete ALL customer profiles\n` +
        `• Delete ALL settings and AI configurations\n` +
        `• Remove the page from your account\n\n` +
        `Are you absolutely sure?`
    );

    if (!firstConfirm) return;

    // Second confirmation (double-check)
    const secondConfirm = window.confirm(
      `⚠️ FINAL WARNING ⚠️\n\n` +
        `You are about to PERMANENTLY DELETE all data for "${currentPage.name}".\n\n` +
        `This includes ${currentPage.messages || 0} messages and all associated data.\n\n` +
        `Type the page name to confirm deletion.\n\n` +
        `Click OK to continue, Cancel to abort.`
    );

    if (!secondConfirm) return;

    setRemovingPageId(pageId);
    setManageDialogPageId(null); // Close dialog

    try {
      const response = await api.delete(
        API_ENDPOINTS.FACEBOOK_PAGE_PERMANENT_DELETE(pageId)
      );

      if (response.data.success) {
        // Remove the page from the list immediately
        setPages(pages.filter((page) => page.id !== pageId));
        
        // Show deletion summary
        const log = response.data.deletionLog;
        alert(
          `✓ Page permanently deleted\n\n` +
          `Deleted data:\n` +
          `• Conversations: ${log.conversations}\n` +
          `• Messages: ${log.messages}\n` +
          `• Comments: ${log.comments}\n` +
          `• Posts: ${log.posts}\n` +
          `• Customer Profiles: ${log.customerProfiles}`
        );
      } else {
        alert(
          `Failed to delete page: ${response.data.error || "Unknown error"}`
        );
      }
    } catch (error: any) {
      console.error("Error permanently deleting page:", error);

      if (error.response?.status === 401) {
        alert(
          "Authentication expired. Please refresh the page and log in again."
        );
      } else if (error.response?.status === 404) {
        alert("Page not found. Please refresh the page list.");
      } else if (error.code === "ERR_NETWORK") {
        alert(
          "Cannot connect to server. Please make sure the backend is running."
        );
      } else {
        alert(
          `Failed to permanently delete page: ${
            error.response?.data?.error || error.message
          }`
        );
      }
    } finally {
      setRemovingPageId(null);
    }
  };

  // COMMENTED OUT FOR FACEBOOK REVIEW - Preserved for re-enabling after approval
  const _handleToggleAutoreply = async (pageId: string, enabled: boolean) => {
    _setTogglingPageId(pageId);
    try {
      const response = await api.post(API_ENDPOINTS.AI_SETTINGS(pageId), {
        autoRepliesEnabled: enabled,
      });

      if (response.data.success) {
        // Update the page in the list
        setPages(
          pages.map((page) =>
            page.id === pageId
              ? {
                  ...page,
                  aiSettings: {
                    ...page.aiSettings,
                    autoRepliesEnabled: enabled,
                    languagePreference:
                      page.aiSettings?.languagePreference || "auto",
                    replyDelay: page.aiSettings?.replyDelay || 10,
                  },
                }
              : page
          )
        );
      } else {
        alert(
          `Failed to ${enabled ? "enable" : "disable"} autoreply: ${
            response.data.error || "Unknown error"
          }`
        );
      }
    } catch (error: any) {
      console.error("Error toggling autoreply:", error);
      alert(
        `Failed to ${enabled ? "enable" : "disable"} autoreply: ${
          error.response?.data?.error || error.message
        }`
      );
    } finally {
      _setTogglingPageId(null);
    }
  };

  const handleToggleModeration = async (pageId: string, enabled: boolean) => {
    setTogglingModerationId(pageId);
    try {
      const response = await api.post(API_ENDPOINTS.AI_SETTINGS(pageId), {
        commentModerationEnabled: enabled,
      });

      if (response.data.success) {
        // Update the page in the list
        setPages(
          pages.map((page) =>
            page.id === pageId
              ? {
                  ...page,
                  aiSettings: {
                    ...page.aiSettings,
                    autoRepliesEnabled: page.aiSettings?.autoRepliesEnabled !== false,
                    commentModerationEnabled: enabled,
                    languagePreference: page.aiSettings?.languagePreference || 'auto',
                    replyDelay: page.aiSettings?.replyDelay || 10,
                  },
                }
              : page
          )
        );
      } else {
        alert(
          `Failed to ${enabled ? "enable" : "disable"} moderation: ${
            response.data.error || "Unknown error"
          }`
        );
      }
    } catch (error: any) {
      console.error("Error toggling moderation:", error);
      alert(
        `Failed to ${enabled ? "enable" : "disable"} moderation: ${
          error.response?.data?.error || error.message
        }`
      );
    } finally {
      setTogglingModerationId(null);
    }
  };

  const activePages = pages.filter((p) => p.status === "active").length;
  // COMMENTED OUT FOR FACEBOOK REVIEW - Total Messages hidden
  const _totalMessages = pages.reduce((sum, page) => sum + page.messages, 0);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading authentication...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center py-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Facebook className="h-6 w-6 text-white" />
                </div>
              </div>
              <div className="ml-4">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                  Your Accounts
                </h1>
                <p className="text-sm text-gray-500">
                  Manage your connected pages and accounts
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3 sm:space-x-4">
              <Button
                onClick={loadPages}
                variant="outline"
                size="sm"
                disabled={isLoading}
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Refresh
              </Button>
              <Button
                onClick={handleConnectPage}
                disabled={isConnecting}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {isConnecting ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Plus className="h-4 w-4 mr-2" />
                )}
                Connect Page
              </Button>
              {instagramEnabled && (
                <Button
                  onClick={handleConnectInstagram}
                  disabled={isConnectingInstagram}
                  className="bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 hover:opacity-90 text-white"
                >
                  {isConnectingInstagram ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Instagram className="h-4 w-4 mr-2" />
                  )}
                  Connect Instagram
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Redirect Message */}
        {redirectState?.message && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6"
          >
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-blue-600 mr-3" />
              <div>
                <p className="text-sm font-medium text-blue-900">
                  {redirectState.message}
                </p>
                {redirectState.from && (
                  <p className="text-xs text-blue-700 mt-1">
                    You'll be redirected back to {redirectState.from} after
                    selecting a page.
                  </p>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Facebook className="h-5 w-5 text-blue-600" />
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-xs font-medium text-gray-500">
                      Connected Pages
                    </p>
                    <p className="text-xl font-bold text-gray-900">
                      {pages.length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="hidden md:block"
          >
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-xs font-medium text-gray-500">
                      Active Pages
                    </p>
                    <p className="text-xl font-bold text-gray-900">
                      {activePages}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* COMMENTED OUT FOR FACEBOOK REVIEW - Only submitting comment moderation */}
          {/* <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                      <MessageSquare className="h-5 w-5 text-purple-600" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">
                      Total Messages
                    </p>
                    <p className="text-2xl font-bold text-gray-900">
                      {totalMessages.toLocaleString()}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div> */}
        </div>

        {/* Pages Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-gray-900">
                Your Facebook Pages
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-4">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="animate-pulse">
                      <div className="flex items-center space-x-4 p-4 border rounded-lg">
                        <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                        <div className="flex-1 space-y-2">
                          <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                          <div className="h-3 bg-gray-200 rounded w-1/6"></div>
                        </div>
                        <div className="w-20 h-6 bg-gray-200 rounded"></div>
                        <div className="w-24 h-8 bg-gray-200 rounded"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : error ? (
                <div className="text-center py-12">
                  <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Connection Error
                  </h3>
                  <p className="text-gray-600 mb-4">{error}</p>
                  <Button onClick={loadPages} variant="outline">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Try Again
                  </Button>
                </div>
              ) : pages.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Facebook className="h-8 w-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No Pages Connected
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Connect your first Facebook page to get started with
                    AI-powered responses
                  </p>
                  <Button
                    onClick={handleConnectPage}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Facebook className="h-4 w-4 mr-2" />
                    Connect Your First Page
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <AnimatePresence>
                    {pages.map((page, index) => (
                      <motion.div
                        key={page.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                        className={`flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between p-4 border rounded-lg transition-colors cursor-pointer ${
                          page.status === 'disconnected' || page.isConnected === false
                            ? 'border-gray-300 bg-gray-50 opacity-75'
                            : 'hover:bg-gray-50'
                        }`}
                        onClick={(e) => handlePageCardClick(page, e)}
                      >
                        <div className="flex items-center space-x-4 min-w-0">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center overflow-hidden ${page.platform === "instagram" ? "bg-pink-100" : "bg-blue-100"}`}>
                            {page.picture ? (
                              <img
                                src={page.picture}
                                alt={`${page.name} logo`}
                                className="w-full h-full object-cover"
                              />
                            ) : page.platform === "instagram" ? (
                              <Instagram className="h-6 w-6 text-pink-600" />
                            ) : (
                              <Facebook className="h-6 w-6 text-blue-600" />
                            )}
                          </div>
                          <div className="min-w-0">
                            <h3 className="font-medium text-gray-900 truncate flex items-center gap-2">
                              <span className="truncate">{page.name}</span>
                              {page.platform === "instagram" ? (
                                <span className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 text-white">
                                  <Instagram className="h-3 w-3" /> Instagram
                                </span>
                              ) : (
                                <span className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-600 text-white">
                                  <Facebook className="h-3 w-3" /> Facebook
                                </span>
                              )}
                            </h3>
                            <p className="text-sm text-gray-500 truncate">
                              {page.platform === "instagram"
                                ? (page.username ? `@${page.username}` : "Instagram account")
                                : page.category}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-6 sm:gap-8 lg:gap-6">
                          <div className="text-center">
                            <p className="text-sm font-medium text-gray-900">
                              {page.followers != null && page.followers !== "" ? page.followers : "—"}
                            </p>
                            <p className="text-xs text-gray-500">Followers</p>
                          </div>
                          {page.platform !== "instagram" && (
                            <div className="text-center">
                              <p className="text-sm font-medium text-gray-900">
                                {page.messages}
                              </p>
                              <p className="text-xs text-gray-500">Messages</p>
                            </div>
                          )}
                          <div className="hidden lg:block text-center">
                            <p className="text-xs text-gray-500">
                              Last Activity
                            </p>
                            <p className="text-sm font-medium text-gray-900">
                              {page.lastActivity}
                            </p>
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-3">
                          {/* Show different buttons for disconnected pages */}
                          {page.status === 'disconnected' || page.isConnected === false ? (
                            <>
                              <div className="flex items-center space-x-2 bg-gray-50 px-3 py-2 rounded">
                                <AlertCircle className="h-4 w-4 text-gray-500" />
                                <span className="text-sm text-gray-600">
                                  Disconnected - Data Preserved
                                </span>
                              </div>
                              
                              <Button
                                onClick={() => {
                                  // Route through the handlers so the plan gate
                                  // applies — reconnecting a disconnected page
                                  // consumes a slot just like a new connect.
                                  if (page.platform === "instagram") {
                                    handleConnectInstagram();
                                  } else {
                                    handleConnectPage();
                                  }
                                }}
                                variant="outline"
                                size="sm"
                                className="text-blue-600 border-blue-300 hover:bg-blue-50"
                                title={`Reconnect via ${page.platform === "instagram" ? "Instagram" : "Facebook"} to restore this ${page.platform === "instagram" ? "account" : "page"}`}
                              >
                                <RefreshCw className="h-4 w-4 mr-1" />
                                Reconnect via {page.platform === "instagram" ? "Instagram" : "Facebook"}
                              </Button>
                              
                              <Button
                                onClick={() => setManageDialogPageId(page.id)}
                                variant="outline"
                                size="sm"
                                className="text-red-600 border-red-300 hover:bg-red-50"
                                title="Remove page options"
                              >
                                <Settings className="h-4 w-4 mr-1" />
                                Remove
                              </Button>
                            </>
                          ) : (
                            <>
                              {/* Moderation Toggle Switch - Only for connected pages */}
                              <div className="flex items-center space-x-2">
                                <span className="text-sm text-gray-700 font-medium">
                                  Moderation
                                </span>
                                {togglingModerationId === page.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                                ) : (
                                  <Switch
                                    checked={page.aiSettings?.commentModerationEnabled !== false}
                                    onCheckedChange={(checked) =>
                                      handleToggleModeration(page.id, checked)
                                    }
                                    disabled={togglingModerationId === page.id}
                                  />
                                )}
                              </div>

                              <Button
                                onClick={() => setManageDialogPageId(page.id)}
                                variant="outline"
                                size="sm"
                                className="text-red-600 border-red-300 hover:bg-red-50"
                                title="Remove page options"
                              >
                                <Settings className="h-4 w-4 mr-1" />
                                Remove Page
                              </Button>
                            </>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Remove Page Dialog */}
      {manageDialogPageId && (
        <Dialog open={!!manageDialogPageId} onOpenChange={(open) => !open && setManageDialogPageId(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                Remove Page: {pages.find(p => p.id === manageDialogPageId)?.name}
              </DialogTitle>
            </DialogHeader>
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600 mb-6">
                Choose an action for this Facebook page:
              </p>
              
              {/* Disconnect Option */}
              <div className="border border-orange-200 rounded-lg p-4 hover:bg-orange-50 transition-colors">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <XCircle className="h-6 w-6 text-orange-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Disconnect Page
                    </h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Hide this page from your dashboard while preserving all data. You can reconnect anytime.
                    </p>
                    <ul className="text-xs text-gray-500 space-y-1 mb-4">
                      <li>✓ All data and settings preserved</li>
                      <li>✓ Can be restored by reconnecting</li>
                    </ul>
                    <Button
                      onClick={() => handleRemovePage(manageDialogPageId)}
                      variant="outline"
                      className="w-full text-orange-600 border-orange-300 hover:bg-orange-50"
                      disabled={removingPageId === manageDialogPageId}
                    >
                      {removingPageId === manageDialogPageId ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <XCircle className="h-4 w-4 mr-2" />
                      )}
                      Disconnect Page
                    </Button>
                  </div>
                </div>
              </div>

              {/* Delete Option */}
              <div className="border border-red-200 rounded-lg p-4 hover:bg-red-50 transition-colors">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Trash2 className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Delete Permanently
                    </h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Permanently remove this page and all associated data. This action cannot be undone.
                    </p>
                    <ul className="text-xs text-gray-500 space-y-1 mb-4">
                      <li>⚠️ All conversations deleted</li>
                      <li>⚠️ All comments and posts removed</li>
                      <li>⚠️ Cannot be recovered</li>
                    </ul>
                    <Button
                      onClick={() => handlePermanentDeletePage(manageDialogPageId)}
                      variant="outline"
                      className="w-full text-red-600 border-red-300 hover:bg-red-50"
                      disabled={removingPageId === manageDialogPageId}
                    >
                      {removingPageId === manageDialogPageId ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Trash2 className="h-4 w-4 mr-2" />
                      )}
                      Delete Permanently
                    </Button>
                  </div>
                </div>
              </div>

              {/* Cancel Button */}
              <Button
                onClick={() => setManageDialogPageId(null)}
                variant="outline"
                className="w-full mt-4"
              >
                Cancel
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Page limit reached — explains the block instead of leaving a dead button */}
      <Dialog open={showLimitDialog} onOpenChange={setShowLimitDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Page limit reached</DialogTitle>
          </DialogHeader>
          <div className="p-6 pt-0 space-y-4">
            <p className="text-sm text-gray-600">
              Your <strong>{subscription?.planName ?? "current"}</strong> plan includes{" "}
              <strong>
                {subscription?.usage.pages.limit ?? 1}{" "}
                {(subscription?.usage.pages.limit ?? 1) === 1 ? "page" : "pages"}
              </strong>
              . Upgrade to connect more.
            </p>
            {subscription && (
              <Progress
                value={subscription.usage.pages.used}
                max={subscription.usage.pages.limit}
                label="Pages connected"
                valueText={`${subscription.usage.pages.used} of ${subscription.usage.pages.limit ?? 0} pages connected`}
                showValue
              />
            )}
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowLimitDialog(false)}>
                Cancel
              </Button>
              <Button className="flex-1" onClick={() => navigate("/pricing")}>
                View plans
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FacebookPages;
